import { message, Modal } from "antd";
import db from "../../../database";
import { logSequenceEvent } from "../../../utils/sequenceAudit";
import { trace } from "../../../utils/debugTrace";
import dayjs from "dayjs";
import { v4 as uuidv4 } from "uuid";
import { getOAuthHeaders } from "../../../constants/oAuthValidation";
import i18next from "i18next";
import * as Sentry from "@sentry/react";
import upsertPOSLog from "../Retail/posLog";
import { createCustomerService, getCustomerService, updateCustomerService, getB2BCustomers } from "../../../services";
import useCustomerReviewStore from "../../../stores/useCustomerReviewStore";
import { buildCustomerChangeLog, buildCustomerChangeReview, CHANGE_LOG_META_KEY, describeExisting, describeFormDetails, describeFormLayout, describeSubmitted, verifyB2BCustomerSaved } from "../../../utils/customerChangeReview";
import usePOSConfigStore from "../../../stores/usePOSConfigStore";
import useCartStore from "../../../stores/useCartStore";
import useTransactionStore from "../../../stores/useTransactionStore";
import useReferenceStore from "../../../stores/useReferenceStore";
import useAuthStore from "../../../stores/useAuthStore";
import { getIsInterState } from "../../../utils/taxUtils";
import { groupItemsByTaxRate } from "./utils/taxCalculations";
import { useHistory } from "react-router-dom";

const baseUsedKeys = new Set([
  "mobile",
  "mobileno",
  "name",
  "email",
  "pincode",
  "firstname",
  "lastname",
  "gender",
  "taxid",
  "anniversarydate",
  "birthday",
  "birthdaydate",
  "lastvisitdate",
  "validity",
  "type",
  "cardnumber",
  "program",
  "loyality",
]);

const hasContactAddressGroup = (retailCustomerGroups, retailCustomerTabs) => {
  const contactTabIds = (retailCustomerTabs || []).filter((tab) => tab.name?.toLowerCase().includes("contact")).map((tab) => tab.id);
  return (retailCustomerGroups || []).some((group) => contactTabIds.includes(group.tabId) && group.name?.toLowerCase().includes("address"));
};

const getUsedKeys = (retailCustomerGroups, retailCustomerTabs) => {
  if (hasContactAddressGroup(retailCustomerGroups, retailCustomerTabs)) {
    const keys = new Set(baseUsedKeys);
    keys.add("street");
    keys.add("city");
    return keys;
  }
  return baseUsedKeys;
};
const getCustomerConfigData = () => {
  const customerConfig = useReferenceStore.getState().customerConfigs || [];
  return JSON.parse(customerConfig[0]?.configuration || "[]");
};

const getFreshConfig = () => {
  const customerConfigData = getCustomerConfigData();
  const retailCustomerGroups = customerConfigData?.fieldGroups || [];
  const retailCustomerFields = customerConfigData?.fields || [];
  const retailCustomerTabs = customerConfigData?.tabs || [];
  const businessTabIds = retailCustomerTabs.filter((tab) => tab.name?.toLowerCase().includes("business")).map((tab) => tab.id);
  const businessGroupIds = retailCustomerGroups.filter((group) => businessTabIds.includes(group.tabId)).map((group) => group.id);
  return { customerConfigData, retailCustomerGroups, retailCustomerFields, retailCustomerTabs, businessTabIds, businessGroupIds };
};

const readReferenceRows = (key) => {
  let rows = [];
  try {
    rows = JSON.parse(localStorage.getItem(key) || "[]");
  } catch (e) {
    rows = [];
  }
  if (!Array.isArray(rows) || !rows.length) rows = useReferenceStore.getState()[key] || [];
  return rows;
};

// BUG-2370: every customer create / update from a form (B2C and B2B) shows the
// cashier every value that will be saved -- Before / After on an update -- and
// waits. Returns { confirmed, changeLog }: confirmed is false only when the cashier
// chose "Go back and edit"; changeLog is the from / to JSON of the confirmed changes
// (null when there is none). A failure to build the review never blocks the save
// (logged, then confirmed). skipReview is for the restaurant quick-create (table /
// reservation / waitlist: name + mobile typed into that screen's own dialog, no
// customer form).
const reviewCustomerSave = async ({
  mode,
  data,
  payloadEntry,
  isB2BEnabled,
  switchToggle,
  tillData,
  oldCustomer,
  wasB2B,
  selectedB2BCustomer,
  selectedInvoiceAddress,
  selectedDeliveryAddress,
  fields,
  isPanCustomer,
  isNriBilling,
  isCreditSale,
  skipReview,
}) => {
  if (skipReview) return { confirmed: true, changeLog: null };
  let review = null;
  try {
    const { retailCustomerFields: freshFields, retailCustomerGroups, retailCustomerTabs, customerConfigData } = getFreshConfig();
    let loyaltyLevels = [];
    try {
      loyaltyLevels = JSON.parse(localStorage.getItem("loyaltyData") || "{}")?.loyaltyLevels || [];
    } catch (e) {
      loyaltyLevels = [];
    }
    const statesData = readReferenceRows("statesData");
    const countriesData = readReferenceRows("countriesData");
    const serverRows = [...(selectedB2BCustomer?.invoiceAddresses || []), ...(selectedB2BCustomer?.deliveryAddresses || [])];
    const after = describeSubmitted({
      data,
      payloadEntry,
      isB2BEnabled,
      switchToggle,
      statesData,
      countriesData,
      storeRegionName: tillData?.tillAccess?.csBunit?.customerAddress?.csRegion?.name || "",
      knownAddressIds: [...serverRows.map((r) => r.s_customer_address_id), selectedInvoiceAddress, selectedDeliveryAddress],
    });
    const before =
      mode === "update" || selectedB2BCustomer
        ? describeExisting({
            customer: mode === "update" ? oldCustomer : null,
            wasB2B,
            serverB2BCustomer: selectedB2BCustomer,
            statesData,
            countriesData,
            billingId: after.billing?.addressId,
            shippingId: after.shipping?.addressId,
          })
        : null;
    const details = describeFormDetails({
      data,
      fields: Array.isArray(fields) && fields.length ? fields : freshFields,
      groups: retailCustomerGroups,
      statesData,
      countriesData,
      oldCustomer: mode === "update" ? oldCustomer : null,
      apiDataSources: customerConfigData?.apiDataSources || [],
      loyaltyLevels,
    });
    // On an update the top rows always have a Before (the customer being edited).
    const beforeTop =
      before ||
      (mode === "update" && oldCustomer ? describeExisting({ customer: oldCustomer, wasB2B, serverB2BCustomer: null, statesData, countriesData }) : null);
    // The popup is drawn like the customer drawer: same tabs, groups and fields, read-only.
    const layout = describeFormLayout({
      data,
      tabs: retailCustomerTabs,
      groups: retailCustomerGroups,
      fields: freshFields,
      statesData,
      countriesData,
      apiDataSources: customerConfigData?.apiDataSources || [],
      loyaltyLevels,
      isB2BEnabled,
      isPanCustomer,
      isNriBilling,
      isCreditSale,
      switchToggle,
    });
    review = buildCustomerChangeReview({ mode, before: beforeTop, after, details, layout });
  } catch (e) {
    console.error("[CustomerReview] could not build the review, saving without it:", e);
    return { confirmed: true, changeLog: null };
  }
  try {
    logSequenceEvent("customer-review-shown", { mode, rows: review.rows, warnings: review.warnings }, db);
  } catch (e) {
    console.error("[CustomerReview] log failed:", e);
  }
  const confirmed = await useCustomerReviewStore.getState().requestReview(review);
  try {
    logSequenceEvent(confirmed ? "customer-review-confirmed" : "customer-review-back", { mode }, db);
  } catch (e) {
    console.error("[CustomerReview] log failed:", e);
  }
  let changeLog = null;
  if (confirmed) {
    try {
      const auth = useAuthStore.getState();
      changeLog = buildCustomerChangeLog(review, { user: auth.userData?.user || auth.username || "" });
    } catch (e) {
      console.error("[CustomerReview] change log not built:", e);
    }
  }
  return { confirmed, changeLog };
};

// After a 200: read the B2B customer back by GSTIN and check the server really holds
// it with the address ids just sent. Always logged; the cashier is told only when it
// was NOT saved. It does not change the cart.
const checkB2BSaveOnServer = ({ flow, isB2BEnabled, gstin, sCustomerId, billingId, shippingId }) => {
  if (!isB2BEnabled) return;
  verifyB2BCustomerSaved({ fetchB2B: getB2BCustomers, gstin, sCustomerId, billingId, shippingId })
    .then((res) => {
      try {
        logSequenceEvent("customer-save-verify", { flow, status: res.status, reason: res.reason || null, gstin, sCustomerId, billingId, shippingId }, db);
      } catch (e) {
        console.error("[CustomerReview] log failed:", e);
      }
      // A save that went through needs no second popup: the usual save message is
      // enough. Only a save the server did not keep is shown to the cashier.
      if (res.status === "not_saved") useCustomerReviewStore.getState().showResult(res);
    })
    .catch((e) => console.error("[CustomerReview] save check failed:", e));
};
const toCamelCase = (str) => {
  if (!str) return "";
  return str
    .replace(/[^a-zA-Z0-9 ]/g, "")
    .replace(/(?:^\w|[A-Z]|\b\w|\s+)/g, (match, index) => (index === 0 ? match.toLowerCase() : match.toUpperCase()))
    .replace(/\s+/g, "");
};

// "Credit Limit" is a dynamically-configured customer field (its exact name
// varies by client config) — identified the same way it already is in
// CustomerFormField.js / EditCustomerDrawer.js / AddCustomerDrawer.js, by
// matching field.name starting with "credit". Resolve its submitted value so
// it can be forwarded to the upsertB2CCustomer mutation: without this the
// server never receives the credit limit the user entered.
const resolveCreditLimitValue = (data, retailCustomerFields) => {
  const creditLimitField = (retailCustomerFields || []).find((f) => f.name?.toLowerCase().replace(/\s+/g, "").startsWith("credit"));
  const value = creditLimitField ? data[toCamelCase(creditLimitField.name)] : data.creditLimit;
  return value !== undefined && value !== null && value !== "" ? value : null;
};

const extractGroupData = (data, groupId) => {
  if (data[groupId]) return data[groupId];
  return data;
};

const parseGroupData = (value) => {
  if (!value) return {};
  if (typeof value === "object") return value; // already parsed
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch (e) {
      console.error("Invalid JSON string:", value);
      return {};
    }
  }
  return {};
};

const getBusinessAddressGroups = () => {
  const { retailCustomerTabs, retailCustomerGroups } = getFreshConfig();
  const businessTab = retailCustomerTabs.find((tab) => tab.name?.toLowerCase().includes("business"));

  if (!businessTab) return {};

  const businessGroups = retailCustomerGroups.filter((group) => group.tabId === businessTab.id);

  const invoiceGroup = businessGroups.find((g) => g.name?.toLowerCase().includes("invoic"));

  const deliveryGroup = businessGroups.find((g) => g.name?.toLowerCase().includes("deliver"));

  return { invoiceGroup, deliveryGroup };
};

const getCountryId = (countryValue, countriesData, fallbackCountryId) => {
  if (!countryValue) return fallbackCountryId;

  const byId = countriesData.find((country) => country.RecordID === countryValue);
  if (byId) return byId.RecordID;

  const byName = countriesData.find((country) => country.Name?.toLowerCase() === countryValue?.toLowerCase());
  if (byName) return byName.RecordID;

  return fallbackCountryId;
};

const buildCustomerListPayload = (
  data,
  selectedB2BCustomer,
  panCustomerInfo,
  tillData,
  gstInfo,
  isPanCustomer,
  isB2BEnabled,
  selectedInvoiceAddress,
  selectedDeliveryAddress,
  switchToggle,
  currentSOrderID,
  existingCustomerAddressIds
) => {
  const { invoiceGroup, deliveryGroup } = getBusinessAddressGroups();
  const rawInvoiceData = invoiceGroup ? data[invoiceGroup.id] : {};
  const rawDeliveryData = deliveryGroup ? data[deliveryGroup.id] : {};
  const gstAddress = gstInfo?.address || {};
  const panNo = gstInfo?.pan;
  const gstLine1 = [gstAddress.buildingNo, gstAddress.buildingName, gstAddress.flatNo].filter(Boolean).join(", ");
  const gstLine2 = [gstAddress.street, gstAddress.locality, gstAddress.district].filter(Boolean).join(", ");
  const gstCity = gstAddress.location || "";
  const invoiceData = parseGroupData(rawInvoiceData);
  const deliveryData = parseGroupData(rawDeliveryData);
  // Content snapshot of the address actually being submitted, in the same
  // shape saved onto cart.customer.customerAddressIds by createCustomer/
  // updateCustomer below. Lets the address-id resolution further down tell
  // "cashier re-saved the same address" apart from "cashier changed it" --
  // only a real change should mint a new address_id; re-saving unchanged
  // details must reuse the existing one instead of minting an orphan address
  // row that was never actually confirmed on the backend (live-confirmed on
  // both KWC and GPR: a freshly-minted uuid sent as sCustomerBillingId either
  // gets FK-rejected outright or silently doesn't match anything real).
  const invoiceContentSnapshot = {
    line1: invoiceData.streetAddress || invoiceData.line1 || gstLine1 || "",
    line2: invoiceData.streetAddress || invoiceData.line2 || gstLine2 || "",
    city: invoiceData.city || gstCity || "",
    pincode: invoiceData.pincode || gstAddress.pincode || "",
    state: invoiceData.state || "",
    country: invoiceData.country || "",
  };
  const deliveryContentSnapshot = {
    line1: deliveryData.streetAddress || deliveryData.line1 || (switchToggle ? gstLine1 : "") || "",
    line2: deliveryData.streetAddress || deliveryData.line2 || (switchToggle ? gstLine2 : "") || "",
    city: deliveryData.city || gstCity || "",
    pincode: deliveryData.pincode || gstAddress.pincode || "",
    state: deliveryData.state || "",
    country: deliveryData.country || "",
  };
  const snapshotMatches = (current, saved) =>
    !!saved && ["line1", "line2", "city", "pincode", "state", "country"].every((k) => (current[k] || "") === (saved[k] || ""));
  // GST-DIAG (BUG-1682/GPR-IRN): this is the exact write site for
  // fin_lin_customer.gst_no (see finLinCustemer below). Logs the top-level
  // `data.gstNo`, each address group's raw keys (to catch a field configured
  // as gstNumber/gstin instead of gstNo, which every read here would miss
  // silently), and the resolved values about to be sent, so a "1,938 of 2,432
  // invoices had empty gst_no" pattern can be traced back to whichever of
  // these three sources was actually empty for a given customer.
  try {
    logSequenceEvent(
      "gst-diag-build-payload",
      {
        topLevelGstNo: data?.gstNo || null,
        rawInvoiceKeys: rawInvoiceData ? Object.keys(rawInvoiceData) : [],
        rawDeliveryKeys: rawDeliveryData ? Object.keys(rawDeliveryData) : [],
        invoiceGstNo: invoiceData?.gstNo || null,
        deliveryGstNo: deliveryData?.gstNo || null,
        resolvedInvoiceGstNo: invoiceData.gstNo || data.gstNo || "",
        resolvedDeliveryGstNo: deliveryData.gstNo || data.gstNo || "",
        isB2BEnabled,
        isPanCustomer,
      },
      db
    );
  } catch (e) {
    // swallow — diagnostics must never affect customer save
  }
  const existingData = localStorage.getItem("customerAddressIds");
  // BUG-1076's fix (below) reuses a previously-created B2B customerId across
  // repeated "Update Customer" clicks on the SAME open cart, because
  // selectedB2BCustomer gets reset to null after the first success. That
  // reuse is only valid for that same cart/transaction — scope it to the
  // current cart's sOrderID so a different customer's later cart (a
  // different sOrderID) can never inherit a stale sCustomerId (BUG-2038
  // follow-up: this cache is the same one that leaked billing/shipping ids).
  let existingAddressIds = null;
  try {
    existingAddressIds = existingData ? JSON.parse(existingData) : null;
  } catch (e) {
    existingAddressIds = null;
  }
  const canReuseExistingCustomerId = !!(existingAddressIds && currentSOrderID && existingAddressIds.sOrderID === currentSOrderID);
  const customerId = selectedB2BCustomer?.customerId || uuidv4().replace(/-/g, "").toUpperCase();

  // BUG-2341-class fix (address-id collision, 2026-09-23): matches the typed
  // content against the customer's ACTUAL existing address rows (not just
  // "there's only one, so reuse it") before ever reusing an id. Previously,
  // whenever a customer had exactly one invoiceAddresses row AND exactly one
  // deliveryAddresses row (true whenever both were originally saved under
  // "Same as Billing", which mints one shared row), a `length === 1` shortcut
  // forced BOTH invoiceAddressId and deliveryAddressId onto that SAME row's
  // address_id regardless of switchToggle or genuinely different typed
  // content -- confirmed live via addr-diag-resolve trace (idsCollide: true).
  // This also applied to the >1-address case whenever no dropdown selection
  // was made: it defaulted to addresses[0]'s id before ever checking content.
  const addressContentMatchesRow = (snapshot, row) => {
    if (!snapshot || !row) return false;
    const norm = (v) => (v ?? "").toString().trim().toLowerCase();
    // BUG-2341 follow-up: state must be part of the match. Without it, two
    // addresses sharing the same line1/line2/city/pincode but a genuinely
    // different state (e.g. the user corrected a wrong state on an existing
    // street) collided onto the same address_id, leaving the backend's stored
    // region out of sync with what was actually typed -- surfaced live as IRN
    // generation failing with "2265: Recipient GSTIN state code does not
    // match with the state code passed in recipient details", or as an empty
    // buyer/ship location when the colliding row was itself blank.
    const stateMatches = norm(snapshot.state) === norm(row.regionName) || norm(snapshot.state) === norm(row.regionId);
    return (
      norm(snapshot.line1) === norm(row.line1) &&
      norm(snapshot.line2) === norm(row.line2) &&
      norm(snapshot.city) === norm(row.city) &&
      norm(snapshot.pincode) === norm(row.pincode) &&
      stateMatches
    );
  };
  const resolveAddressId = (explicitSelection, addressRows, contentSnapshot, cachedSnapshot, cachedId) =>
    explicitSelection ||
    (addressRows || []).find((row) => addressContentMatchesRow(contentSnapshot, row))?.s_customer_address_id ||
    (snapshotMatches(contentSnapshot, cachedSnapshot) && cachedId) ||
    null;

  let invoiceAddressId = null;
  let deliveryAddressId = null;
  if (switchToggle) {
    const sameAddressId =
      selectedInvoiceAddress ||
      selectedDeliveryAddress ||
      [...(selectedB2BCustomer?.invoiceAddresses || []), ...(selectedB2BCustomer?.deliveryAddresses || [])].find((row) => addressContentMatchesRow(invoiceContentSnapshot, row))
        ?.s_customer_address_id ||
      (snapshotMatches(invoiceContentSnapshot, existingCustomerAddressIds?.invoiceSnapshot) && existingCustomerAddressIds?.billingId) ||
      (snapshotMatches(invoiceContentSnapshot, existingCustomerAddressIds?.deliverySnapshot) && existingCustomerAddressIds?.shippingId) ||
      uuidv4().replace(/-/g, "").toUpperCase();
    invoiceAddressId = sameAddressId;
    deliveryAddressId = sameAddressId;
  } else {
    invoiceAddressId = resolveAddressId(
      selectedInvoiceAddress,
      selectedB2BCustomer?.invoiceAddresses,
      invoiceContentSnapshot,
      existingCustomerAddressIds?.invoiceSnapshot,
      existingCustomerAddressIds?.billingId
    );
    deliveryAddressId = resolveAddressId(
      selectedDeliveryAddress,
      selectedB2BCustomer?.deliveryAddresses,
      deliveryContentSnapshot,
      existingCustomerAddressIds?.deliverySnapshot,
      existingCustomerAddressIds?.shippingId
    );
    if (!invoiceAddressId) invoiceAddressId = uuidv4().replace(/-/g, "").toUpperCase();
    if (!deliveryAddressId) deliveryAddressId = uuidv4().replace(/-/g, "").toUpperCase();
  }

  // ADDR-DIAG (delivery-address-not-persisting investigation, 2026-09-23):
  // traces which branch resolved invoiceAddressId/deliveryAddressId and the
  // exact content about to be attached to each. Suspected root cause: when a
  // B2B customer already has exactly one invoiceAddresses row AND exactly one
  // deliveryAddresses row (true whenever both were originally saved under the
  // "Same as Billing" toggle, which mints a single shared address row for
  // both), the `length === 1` branches above force BOTH invoiceAddressId and
  // deliveryAddressId to that SAME existing address_id even when switchToggle
  // is now off and fresh, distinct content was typed for each -- the new
  // content never gets a distinct id to travel on, and the customerAddress
  // array below then submits two different content bodies under one shared
  // address_id. idsCollide + contentDiffers both true is the fingerprint.
  try {
    const addrDiagData = {
      switchToggle: !!switchToggle,
      invoiceAddressesCount: selectedB2BCustomer?.invoiceAddresses?.length ?? null,
      deliveryAddressesCount: selectedB2BCustomer?.deliveryAddresses?.length ?? null,
      invoiceAddressId,
      deliveryAddressId,
      idsCollide: !!invoiceAddressId && invoiceAddressId === deliveryAddressId,
      invoiceContentSnapshot,
      deliveryContentSnapshot,
      contentDiffers: JSON.stringify(invoiceContentSnapshot) !== JSON.stringify(deliveryContentSnapshot),
    };
    logSequenceEvent("addr-diag-resolve", addrDiagData, db);
    trace("AddrDiag", "resolve", addrDiagData);
  } catch (e) {
    // swallow — diagnostics must never affect customer save
  }

  let statesData = [];
  let countriesData = [];
  try {
    statesData = JSON.parse(localStorage.getItem("statesData") || "[]");
  } catch (e) {
    statesData = [];
  }
  try {
    countriesData = JSON.parse(localStorage.getItem("countriesData") || "[]");
  } catch (e) {
    countriesData = [];
  }
  if (!statesData.length) statesData = useReferenceStore.getState().statesData || [];
  if (!countriesData.length) countriesData = useReferenceStore.getState().countriesData || [];
  const defaultCountryId = tillData?.tillAccess?.csBunit?.customerAddress?.csCountry?.csCountryID || null;
  const defaultRegionId = tillData?.tillAccess?.csBunit?.customerAddress?.csRegion?.csRegionID || null;
  const findCountryId = (name) => countriesData.find((c) => c.Name === name)?.RecordID || defaultCountryId;
  const findStateId = (name) => statesData.find((s) => s.Name === name)?.RecordID || defaultRegionId;
  const getRegionId = (stateValue, statesData, fallbackRegionId) => {
    if (!stateValue) return fallbackRegionId;

    // 1️⃣ Check if it's already an ID (direct match)
    const byId = statesData.find((state) => state.RecordID === stateValue);
    if (byId) return byId.RecordID;

    // 2️⃣ Check if it's a Name (case insensitive safer)
    const byName = statesData.find((state) => state.Name?.toLowerCase() === stateValue?.toLowerCase());
    if (byName) return byName.RecordID;

    // 3️⃣ Fallback
    return fallbackRegionId;
  };
  const customerPayload = [
    {
      sCustomerId: canReuseExistingCustomerId ? existingAddressIds.customerId : customerId,
      name: data.companyName || "",
      csBunitId: tillData?.tillAccess?.csBunit?.csBunitId || null,
      sCustomerCategoryId: null,
      gstNo: data.gstNo || "",
      linkB2bCustomer: isPanCustomer && isB2BEnabled ? "Y" : "N",
      legalName: data.legalName || "",
      customerAddress: [
        {
          isInvoiced: "Y",
          address_id: invoiceAddressId,
          line1: invoiceData.streetAddress || invoiceData.line1 || gstLine1 || "",
          line2: invoiceData.streetAddress || invoiceData.line2 || gstLine2 || "",
          city: invoiceData.city || gstCity || "",
          postalcode: invoiceData.pincode || gstAddress.pincode || "",
          csCountryId: getCountryId(invoiceData.country, countriesData, tillData.tillAccess.csBunit.customerAddress?.csCountry?.csCountryID),
          phone: data.mobileno || "",
          csRegionId: getRegionId(invoiceData.state, statesData, tillData.tillAccess.csBunit.customerAddress?.csRegion?.csRegionID) || null,
        },
        {
          isInvoiced: "N",
          address_id: deliveryAddressId,
          line1: deliveryData.streetAddress || deliveryData.line1 || (switchToggle ? gstLine1 : "") || "",
          line2: deliveryData.streetAddress || deliveryData.line2 || (switchToggle ? gstLine2 : "") || "",
          city: deliveryData.city || gstCity || "",
          postalcode: deliveryData.pincode || gstAddress.pincode || "",
          csCountryId: getCountryId(deliveryData.country, countriesData, tillData.tillAccess.csBunit.customerAddress?.csCountry?.csCountryID),
          phone: data.mobileno || "",
          csRegionId: getRegionId(deliveryData.state, statesData, tillData.tillAccess.csBunit.customerAddress?.csRegion?.csRegionID) || null,
        },
      ],
      finLinCustomer: [
        {
          gstType: invoiceData.gstType || "",
          gstNo: invoiceData.gstNo || data.gstNo || "",
          pan: invoiceData.pan || panNo || "",
          companyType: invoiceData.companyType || "",
          addressId: invoiceAddressId,
        },
        {
          gstType: deliveryData.gstType || "",
          gstNo: deliveryData.gstNo || data.gstNo || "",
          pan: deliveryData.pan || panNo || "",
          companyType: deliveryData.companyType || "",
          addressId: deliveryAddressId,
        },
      ],
    },
  ];
  return {
    customerPayload,
    // BUG-2038 follow-up (2026-09-10): must mirror the sCustomerId actually
    // sent in customerPayload above (line ~293), not the raw candidate --
    // when canReuseExistingCustomerId is true the mutation reuses the real,
    // already-persisted id while this used to return the fabricated one,
    // which then got written into customerAddressIds/newB2bCustomerId and
    // sent to posOrderProcessor1 as sCustomerId for an id that was never
    // actually created in s_customer, causing an FK violation on sync.
    customerId: canReuseExistingCustomerId ? existingAddressIds.customerId : customerId,
    invoiceAddressId,
    deliveryAddressId,
    invoiceContentSnapshot,
    deliveryContentSnapshot,
  };
};

const buildGraphQLJSONString = (obj) => {
  return JSON.stringify(obj).replace(/"/g, '\\"');
};

// The backend surfaces unique-constraint violations (e.g. duplicate mobile/code)
// as a raw DB exception string in the mutation's `message` field or in a caught
// axios error, instead of a clean status. Detect that signature so the UI can
// show a user-friendly message rather than the SQL/stack-trace text (BUG-1854).
const isDuplicateCustomerError = (msg) => {
  if (!msg) return false;
  const lower = String(msg).toLowerCase();
  return (
    lower.includes("duplicatekeyexception") ||
    lower.includes("duplicate key value") ||
    lower.includes("code_unique") ||
    (lower.includes("unique constraint") && lower.includes("customer"))
  );
};

const extractGroupedMetaData = (metaData) => {
  if (!Array.isArray(metaData)) return {};

  const { retailCustomerGroups } = getFreshConfig();
  const grouped = {};

  metaData.forEach((item) => {
    const { key, value } = item;

    // only group keys
    if (/^group_\d+$/.test(key)) {
      const group = retailCustomerGroups.find((g) => g.id === key);

      if (!group) return;

      let parsedValue = {};

      try {
        parsedValue = typeof value === "string" ? JSON.parse(value) : value;
      } catch (e) {
        console.error("Invalid JSON in group:", value);
      }

      if (Object.keys(parsedValue).length > 0) {
        grouped[key.toLowerCase()] = parsedValue;
      }
    }
  });

  return grouped;
};

const getAddressFromContactGroup = (data, retailCustomerGroups, retailCustomerTabs) => {
  const contactTabIds = retailCustomerTabs.filter((tab) => tab.name?.toLowerCase().includes("contact")).map((tab) => tab.id);

  const contactAddressGroups = retailCustomerGroups.filter((group) => contactTabIds.includes(group.tabId) && group.name?.toLowerCase().includes("address"));

  for (const group of contactAddressGroups) {
    const rawGroupData = data[group.id];
    const groupData = parseGroupData(rawGroupData);
    if (groupData && Object.keys(groupData).length > 0) {
      return {
        line1: groupData.line1 || null,
        line2: groupData.line2 || null,
        city: groupData.city || null,
        state: groupData.state || null,
      };
    }
  }

  return {};
};

export const createCustomer = async (
  data,
  cart,
  setCart,
  form,
  tillLayout,
  closeCustomerSearch,
  tillData,
  posLogActivity,
  setKioskUI,
  setDisplayAddNewCustomer,
  UAECustomerForm,
  customerForm,
  setTillLink,
  setCustomerLoading,
  setDisplayUAECustomer,
  isCallCenterFlow,
  setCallCenterVisible,
  setIsCallCenterFlow,
  history,
  setIsSubmittingCustomer,
  setLastSearchedValue,
  setSelectedB2BCustomer,
  selectedB2BCustomer,
  setSwitchToggle,
  setFormDataCache,
  isPanCustomer,
  setIsPanCustomer,
  isB2BEnabled,
  setPanCustomerInfo,
  panCustomerInfo,
  pendingRefundCustomer,
  setPendingRefundCustomer,
  isRefundCustomerEdit,
  setIsRefundCustomerEdit,
  isRefundWalletOpen,
  setIsRefundWalletOpen,
  isNriBilling,
  setIsNriBilling,
  isCreditSale,
  setIsCreditSale,
  panDrawerMode,
  documents,
  setGstInfo,
  retailCustomerFields,
  gstInfo,
  selectedInvoiceAddress,
  selectedDeliveryAddress,
  setSelectedInvoiceAddress,
  setSelectedDeliveryAddress,
  switchToggle,
  reviewOptions = {}
) => {
  const isExchange = useTransactionStore.getState().selectedReturnType === "EXCHANGE";
  let setAuthTokens;
  const t = i18next.t;
  const authHeaders = await getOAuthHeaders();
  if (authHeaders && authHeaders.access_token) {
    setAuthTokens = authHeaders.access_token;
  }
  if (data["Tax Id"] && !data.taxId) data.taxId = data["Tax Id"];
  if (data.taxid && !data.taxId) data.taxId = data.taxid;
  if (data.taxId && !data.taxID) data.taxID = data.taxId;

  const { retailCustomerGroups, retailCustomerFields: freshRetailFields, retailCustomerTabs, businessTabIds, businessGroupIds } = getFreshConfig();
  const activeRetailFields = retailCustomerFields || freshRetailFields;
  const creditLimitValue = resolveCreditLimitValue(data, activeRetailFields);
  const ungroupedBusinessFields = activeRetailFields.filter((field) => businessTabIds.includes(field.tabId) && (!field.groupId || !businessGroupIds.includes(field.groupId)));
  const ungroupedFieldsObj = ungroupedBusinessFields.reduce((acc, field) => {
    const camelCaseKey = toCamelCase(field.name);
    const value = data[camelCaseKey];
    if (value !== "" && value !== null && value !== undefined) {
      acc[field.name?.trim() || field.id] = String(value);
    }
    return acc;
  }, {});
  const groupedCustomerListObj = Object.entries(data)
    .filter(([key]) => businessGroupIds.includes(String(key)))
    .reduce((acc, [groupId, groupData]) => {
      const group = retailCustomerGroups.find((g) => g.id === groupId);
      const groupName = group?.name?.trim() || "";

      if (typeof groupData === "object" && groupData !== null) {
        const cleanedFields = Object.entries(groupData).reduce((fieldAcc, [fieldKey, fieldValue]) => {
          if (fieldValue !== "" && fieldValue !== null && fieldValue !== undefined) {
            fieldAcc[fieldKey.trim()] = String(fieldValue);
          }
          return fieldAcc;
        }, {});

        if (Object.keys(cleanedFields).length > 0) {
          acc[groupName] = cleanedFields;
        }
      }
      return acc;
    }, {});
  let customerListObj = {};
  if (isPanCustomer) {
    customerListObj = Object.entries(data).reduce((acc, [key, value]) => {
      if (value !== "" && value !== null && value !== undefined) {
        acc[key] = String(value);
      }
      return acc;
    }, {});
  } else {
    customerListObj = {
      ...ungroupedFieldsObj,
      ...groupedCustomerListObj,
    };
  }

  // const stringifiedData = JSON.stringify(customerListObj);
  // const newStringifiedFields = stringifiedData.replace(/\\"/g, '\\"');
  // const newStringRequest = JSON.stringify(newStringifiedFields);

  // Only build customer list payload when both PAN and B2B are enabled
  let customerPayload = [];
  let customerId = null;
  let billingAddressId = null;
  let shippingAddressId = null;
  let invoiceContentSnapshot = null;
  let deliveryContentSnapshot = null;
  if (isB2BEnabled) {
    const activeCartForCustomerAnchor = isExchange ? useCartStore.getState().exchangeCart || cart : cart;
    const payloadData = buildCustomerListPayload(
      data,
      selectedB2BCustomer,
      panCustomerInfo,
      tillData,
      gstInfo,
      isPanCustomer,
      isB2BEnabled,
      selectedInvoiceAddress,
      selectedDeliveryAddress,
      switchToggle,
      activeCartForCustomerAnchor?.sOrderID,
      // A brand-new customer has nothing here yet (null); a customer already
      // attached to this cart from an earlier create/update in the SAME
      // session carries the address ids buildCustomerListPayload just resolved
      // last time (see the customerAddressIds attach below) -- reusing them
      // instead of minting a fresh uuid every time this runs is the actual fix.
      activeCartForCustomerAnchor?.customer?.customerAddressIds
    );
    customerPayload = payloadData.customerPayload;
    customerId = payloadData.customerId;
    billingAddressId = payloadData.invoiceAddressId;
    shippingAddressId = payloadData.deliveryAddressId;
    invoiceContentSnapshot = payloadData.invoiceContentSnapshot;
    deliveryContentSnapshot = payloadData.deliveryContentSnapshot;
  }

  // BUG-2370: show the cashier what will be saved before any flag / address id
  // is written. "Go back and edit" leaves the drawer and form as they are.
  const { confirmed: reviewConfirmed, changeLog: customerChangeLog } = await reviewCustomerSave({
    mode: "create",
    data,
    payloadEntry: customerPayload?.[0],
    isB2BEnabled,
    switchToggle,
    tillData,
    oldCustomer: null,
    wasB2B: false,
    selectedB2BCustomer,
    selectedInvoiceAddress,
    selectedDeliveryAddress,
    fields: activeRetailFields,
    isPanCustomer,
    isNriBilling,
    isCreditSale,
    skipReview: !!reviewOptions?.skipReview,
  });
  if (!reviewConfirmed) {
    setCustomerLoading(false);
    return false;
  }

  if (isB2BEnabled) {
    const activeCartForCustomerAnchor = isExchange ? useCartStore.getState().exchangeCart || cart : cart;
    // BUG-2038: always overwrite (not just when unset) — otherwise only the
    // first customer served in a session ever gets their address ids stored,
    // and every later customer silently inherits that first customer's ids
    // via the captureOrderCreationSnapshot/useOrderProcessing.js live reads.
    // sOrderID is stamped alongside so a later, different cart can never
    // reuse this customerId as sCustomerId (see buildCustomerListPayload).
    localStorage.setItem(
      "customerAddressIds",
      JSON.stringify({
        customerId: customerId,
        billingId: billingAddressId,
        shippingId: shippingAddressId,
        sOrderID: activeCartForCustomerAnchor?.sOrderID,
        invoiceSnapshot: invoiceContentSnapshot,
        deliverySnapshot: deliveryContentSnapshot,
      })
    );
  }
  const newStringRequest = buildGraphQLJSONString(customerPayload);

  // ADDR-DIAG: exact customerAddress array as it's about to be serialized
  // into the `customerList` string sent to upsertB2CCustomer (create path).
  try {
    const wirePayload = { flow: "create", customerAddress: customerPayload?.[0]?.customerAddress || null };
    logSequenceEvent("addr-diag-wire-payload", wirePayload, db);
    trace("AddrDiag", "wire-payload", wirePayload);
  } catch (e) {
    // swallow — diagnostics must never affect customer save
  }

  // BUG-2601: the Add drawer's switch no longer writes local storage on toggle, so the
  // save writes both (same as updateCustomer). Read later by the /estimate customer tag,
  // the drawer's next opening and the stock region check.
  if (isB2BEnabled) {
    localStorage.setItem("isB2bEnabled", "true");
    useTransactionStore.getState().setIsB2bEnabled(String(isB2BEnabled));
  } else {
    localStorage.removeItem("isB2bEnabled");
    useTransactionStore.getState().setIsB2bEnabled(null);
  }

  if (isCreditSale) {
    useTransactionStore.getState().setIsCreditSale(String(isCreditSale));
  } else {
    localStorage.removeItem("isCreditSale");
    useTransactionStore.getState().setIsCreditSale(null);
  }

  if (isPanCustomer) {
    localStorage.setItem("isPanCustomer", String(isPanCustomer));
  }

  if (isNriBilling) {
    localStorage.setItem("isNriBilling", String(isNriBilling));
  }

  function toNormalCase(key) {
    return key
      .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
      .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
      .replace(/^./, (str) => str.toUpperCase());
  }
  const EXCLUDED_META_KEYS = new Set([
    "customerdocumenturls", "pancardurl", "customer_document_urls", "pan_card_url", "passporturl", "passport_url", "scustomer",
    "creditapproachpercentage", "creditbalance", "creditlimit", "iscredit", "customeraddressids",
    "customerchangelog",
  ]);
  const usedKeys = getUsedKeys(retailCustomerGroups, retailCustomerTabs);
  const ungroupedBusinessKeys = ungroupedBusinessFields.map((field) => toCamelCase(field.name));
  const businessFieldKeysSet = new Set(ungroupedBusinessKeys.map((k) => k.toLowerCase()));
  const metaData = Object.entries(data)
    .filter(([key, value]) => {
      const normalizedKey = key.trim().toLowerCase().replace(/[_\s]/g, "");
      const lowerKey = key.toLowerCase();
      // grouped business fields
      const isGroupedBusinessField = businessGroupIds.includes(String(key));
      // ungrouped business fields
      const isUngroupedBusinessField = businessFieldKeysSet.has(lowerKey);
      const isBusinessField = isGroupedBusinessField || isUngroupedBusinessField;
      // MAIN CONDITION
      // if (isBusinessField && !(isPanCustomer && isB2BEnabled)) {
      //   return false;
      // }
      // if (isUngroupedBusinessField && !(isPanCustomer && isB2BEnabled)) {
      //   return false;
      // }

      return !usedKeys.has(key.trim().toLowerCase()) && !EXCLUDED_META_KEYS.has(normalizedKey) && value != null && value !== "";
    })
    .map(([key, value]) => ({
      key: /^group_\d+$/i.test(key) ? key.toLowerCase() : toNormalCase(key),
      value: typeof value === "object" ? JSON.stringify(value) : typeof value === "string" ? value.trim() : String(value),
    }));
  // ADD CUSTOMER DOCUMENT URLS
  if (documents && documents.length > 0) {
    const documentUrls = documents.filter((doc) => doc?.docUrl && doc.docName === "Customer Document").map((doc) => doc.docUrl);

    if (documentUrls.length > 0) {
      metaData.push({
        key: "CUSTOMER_DOCUMENT_URLS",
        value: JSON.stringify(documentUrls),
      });
    }
  }

  // PAN URL SHOULD BE ADDED SEPARATELY
  if (documents && documents.length > 0) {
    const panDoc = documents.find((doc) => doc.docName === "PAN Card");

    if (panDoc?.docUrl) {
      metaData.push({
        key: "PAN_CARD_URL",
        value: panDoc.docUrl,
      });
    }
  }
  if (documents && documents.length > 0) {
    const passportDoc = documents.find((doc) => doc.docName === "Passport");
    if (passportDoc?.docUrl) {
      metaData.push({
        key: "PASSPORT_URL",
        value: passportDoc.docUrl,
      });
    }
  }
  // BUG-2370: from / to of what the cashier confirmed in the review popup.
  if (customerChangeLog) metaData.push({ key: CHANGE_LOG_META_KEY, value: customerChangeLog });
  const loyaltyLevel =
    retailCustomerFields && retailCustomerFields.length > 0
      ? (data.loyaltyLevel?.cwrLoyaltyLevelId ?? data.loyaltyLevel) || data.program
      : data.program && data.program !== "" && data.loyality === true
        ? data.program
        : null;
  const posConfig = usePOSConfigStore.getState().posConfig;
  const serverUrl = process.env.REACT_APP_serverUrl;
  const newCustomerId = uuidv4().replace(/-/g, "").toUpperCase();

  const mobileNo = data.mobile || data.mobileno ? data.mobile || data.mobileno : null;
  if (mobileNo) {
    const paramsInput = {
      query: `query{
          customerSearch(searchKey:"${mobileNo}",bunitId:"${tillData?.tillAccess?.csBunit?.csBunitId}"){
            cwrCustomerId
            sCustomerAddress
            localisation
            sPriceListId 
            code   
            name
            email        
            mobileNo
            sCustomerAddress
            cardNumber
            regType
            validity
            name
            creditLimit
            creditBalance
            creditApproachPercentage
            pincode
            retlLoyaltyBalance
            b2cRegisteredstoreId
             iscredit
             lastVisitDate
             noOfVisits
             lastBillingAmount
             averageBasketValue
             loyaltyPointToExpire
             firstName
              lastName
               gender
                taxId
            anniversaryDate
             birthday
            loyaltyLevel{
                cwrLoyaltyLevelId
                name
                accumulationRate
                redemptionRate
            }
            cwrWallet {
                 wallet_Id
                balance_Amt
            }
            sCustomer{
              sCustomerID
              customerCategory{
                sCustomerCateforyId
                value
                name
                description
              }
            }
            customerAddress {
                line1 
                line2 
                city 
                region
                 country 
                 csCountryId
                  csRegionId
            }
            csBunit{
              csBunitId
              name
            }
            b2cCustomerSegment{
              name
              cwrB2CCustomerSegmentId
            }
            sPriceListId
            priceListName
            metaData {
              key
              value
            }
          }
        }`,
    };
    try {
      const response = await getCustomerService(paramsInput);
      const customerSearchResults = response?.data?.data?.customerSearch;
      if (customerSearchResults.length > 0) {
        message.warning(`Customer already exists with mobile number ${mobileNo}`);

        setCustomerLoading(false);
        return;
      }
    } catch (error) {
      Sentry.captureException(error);
      console.error("Customer search failed:", error);
      message.error("Unable to validate mobile number.");
      return;
    }
  }

  const contactAddress = getAddressFromContactGroup(data, retailCustomerGroups, retailCustomerTabs);

  // Trim leading/trailing whitespace before it reaches the mutation payload (BUG-1884).
  const trimIfString = (value) => (typeof value === "string" ? value.trim() : value);
  const rawName = trimIfString(data.name || data.customerName);
  // When only the Name field is filled (First Name/Last Name left blank), derive
  // firstName/lastName from it so upsertB2CCustomer doesn't receive them as null (BUG-1888).
  const nameParts = rawName ? rawName.split(/\s+/).filter(Boolean) : [];
  const firstNameInput = trimIfString(data.firstName) || "";
  const lastNameInput = trimIfString(data.lastName) || "";
  // Only derive from the Name field when BOTH First/Last are unset — if one of them was
  // actually filled in, nameParts (built off the pre-trim Name field) can duplicate it.
  const deriveFromName = !firstNameInput && !lastNameInput;
  const trimmedFirstName = deriveFromName ? nameParts[0] || "" : firstNameInput;
  const trimmedLastName = deriveFromName ? nameParts.slice(1).join(" ") : lastNameInput;
  // Rebuild from the already-trimmed parts (BUG-1884) instead of trimming rawName as a
  // whole: rawName can carry interior whitespace (e.g. an untrimmed First Name joined
  // with Last Name upstream), which outer-edge trimming alone would not collapse.
  const trimmedName = [trimmedFirstName, trimmedLastName].filter(Boolean).join(" ") || rawName;

  const newRequest = {
    query: `mutation {
        upsertB2CCustomer(
          id: "${newCustomerId}"
          type: "New"
          customer: {
            csBunitId: "${tillData.tillAccess.csBunit.csBunitId}",
            csClientId: "${tillData.tillAccess.csClientId}",
            createdby: "${tillData.tillAccess.csUserId}",
            updatedby: "${tillData.tillAccess.csUserId}",
            code: ${data.mobile ? `"${data.mobile.toLowerCase()}"` : null},
            name: ${trimmedName ? `"${trimmedName}"` : null},
            mobileNo: ${data.mobile || data.mobileno ? `"${data.mobile || data.mobileno}"` : null},
            email: ${data.email ? `"${data.email}"` : null},
            pincode: ${data.pincode ? data.pincode : null},
            sCustomerId: ${isB2BEnabled && selectedB2BCustomer?.customerId ? `"${selectedB2BCustomer.customerId}"` : null},
            finPaymentmethodId: null,
            retlLoyaltyBalance: null,
            iscredit: ${isCreditSale ? "true" : "false"},
            creditLimit: ${isCreditSale && creditLimitValue !== null ? creditLimitValue : null},
            b2cRegisteredstoreId: "${tillData.tillAccess.csBunit.csBunitId}",
            firstName: ${trimmedFirstName ? `"${trimmedFirstName}"` : null},
            lastName: ${trimmedLastName ? `"${trimmedLastName}"` : null},
            gender: ${data.gender && data.gender !== "null" ? `"${data.gender}"` : null},
            taxId: ${data.taxID ? `"${data.taxID}"` : null},
            anniversaryDate: ${
              data.anniversaryDate !== undefined && data.anniversaryDate !== null && data.anniversaryDate !== "" ? `"${dayjs(data.anniversaryDate).format("YYYY-MM-DD")}"` : null
            },
            birthday: ${
              data.BirthdayDate || data.birthday
                ? dayjs(data.BirthdayDate || data.birthday).isValid()
                  ? `"${dayjs(data.BirthdayDate || data.birthday).format("YYYY-MM-DD")}"`
                  : null
                : null
            },
            lastVisitDate: ${data.lastVisitDate !== undefined && data.lastVisitDate !== null ? `"${dayjs(data.lastVisitDate).format("YYYY-MM-DD")}"` : null},
            noOfVisits: 0,
            validity: ${dayjs(data.validity).isValid() ? `"${dayjs(data.validity).format("YYYY-MM-DD")}"` : null},
            regType: ${data.type ? `"${data.type}"` : null},
            cardNumber: ${data.cardNumber ? `"${data.cardNumber}"` : null},
            lastBillingAmount: 0,
            averageBasketValue: 0,
            loyaltyPointToExpire: null,
            metaData: ${JSON.stringify(metaData)
              .replace(/"key":/g, "key:")
              .replace(/"value":/g, "value:")
              .replace(/"/g, '"')},
            loyaltyLevel: { cwrLoyaltyLevelId:  ${loyaltyLevel && loyaltyLevel !== null && loyaltyLevel !== "undefined" ? `"${loyaltyLevel}"` : null} }
            customerAddress: {
              line1: ${retailCustomerFields && retailCustomerFields.length > 0 ? (contactAddress?.line1 ? `"${contactAddress.line1}"` : data.street ? `"${data.street}"` : null) : data.street ? `"${data.street}"` : null},
              line2: ${retailCustomerFields && retailCustomerFields.length > 0 ? (contactAddress?.line2 ? `"${contactAddress.line2}"` : null) : data.city ? `"${data.city}"` : null},
              city: ${retailCustomerFields && retailCustomerFields.length > 0 ? (contactAddress?.city ? `"${contactAddress.city}"` : data.city ? `"${data.city}"` : null) : data.city ? `"${data.city}"` : null},
              csRegionId: ${
                retailCustomerFields && retailCustomerFields.length > 0
                  ? contactAddress?.state
                    ? `"${contactAddress.state}"`
                    : `"${tillData.tillAccess.csBunit.customerAddress.csRegion.csRegionID}"`
                  : tillData.tillAccess.csBunit.customerAddress?.csRegion?.csRegionID
                    ? `"${tillData.tillAccess.csBunit.customerAddress.csRegion.csRegionID}"`
                    : null
              },
              csCountryId: ${tillData.tillAccess.csBunit.customerAddress?.csCountry?.csCountryID ? `"${tillData.tillAccess.csBunit.customerAddress.csCountry.csCountryID}"` : null},
            }
            b2bCustomer: "${isB2BEnabled ? "Y" : "N"}",
            customerList: "${newStringRequest}"
          }
        ) {
          cwrCustomerId
          sPriceListId
          code
          name
          email
          mobileNo
          pincode
          retlLoyaltyBalance
          b2cRegisteredstoreId
          iscredit
          creditLimit
          creditBalance
          lastVisitDate
          noOfVisits
          lastBillingAmount
          averageBasketValue
          loyaltyPointToExpire
          firstName
          lastName
          gender
          taxId
          cardNumber
          regType
          validity
          anniversaryDate
          birthday
          sCustomer {
            sCustomerID
            customerCategory {
              sCustomerCateforyId
              value
              name
              description
            }
          }
          csBunit {
            csBunitId
            name
          }
          loyaltyLevel {
            cwrLoyaltyLevelId
          }
          customerAddress {
            line1
            line2
            city
            region
            country
            csCountryId
            csRegionId
          }
          status
          message
          metaData {
            key
            value
          }
        }
      }`,
  };

  try {
    setCustomerLoading(true);
    const response = await createCustomerService(newRequest);

    const result = response?.data?.data?.upsertB2CCustomer;

    if (result?.status === "200") {
      if (tillLayout === 3) {
        localStorage.setItem("kioskUI", 3);
        setKioskUI(3);
        const customEvent = new CustomEvent("customStorageChange", {
          detail: { key: "kioskUI", newValue: 3 },
        });
        window.dispatchEvent(customEvent);
      }

      form.resetFields();
      // Previously reset unconditionally here on every save, regardless of
      // whether invoice/delivery were actually the same -- made the toggle
      // show "off" right after registering with it "on," even though nothing
      // about the saved address changed. Reset only belongs at order
      // completion (see useOrderProcessing.js's checkout-completion cleanup,
      // matching the existing isB2bEnabled reset pattern from BUG-1969 F1),
      // not here.
      customerForm.resetFields();
      UAECustomerForm.resetFields();
      setDisplayAddNewCustomer(false);
      setDisplayUAECustomer(false);
      setTillLink(false);
      setFormDataCache({});
      if (panDrawerMode !== "VALIDATION") {
        setPanCustomerInfo(null);
        setIsPanCustomer(false);
      }
      setIsSubmittingCustomer(false);
      setLastSearchedValue("");
      if (typeof setGstInfo === "function") setGstInfo(null);
      setSelectedB2BCustomer(null);
      if (setSelectedInvoiceAddress) setSelectedInvoiceAddress(null);
      if (setSelectedDeliveryAddress) setSelectedDeliveryAddress(null);
      const saleTypeData = usePOSConfigStore.getState().saleTypeData || {};
      const selectedPriceListId = result?.sPriceListId ?? saleTypeData?.customer?.sPriceListId;
      const cartCustomer = {
        ...result,
        sPriceListId: selectedPriceListId,
        newB2bCustomerId: customerId,
      };
      if (useTransactionStore.getState().refundCustomerEdit === "Y" && cart?.isReturn === true) {
        useTransactionStore.getState().setRefundReturnCustomerId(result);

        useTransactionStore.getState().setRefundCustomerEdit(null);
        setIsRefundWalletOpen(true); // reopen wallet drawer
        closeCustomerSearch();
        return;
      }

      const updatedMetaData = result?.metaData || [];

      // GST-DIAG (BUG-1682/GPR-IRN): what the backend actually saved/echoed
      // back for this new customer, closing the loop against the
      // gst-diag-build-payload log above (what we sent). If gstNo shows up
      // here as empty despite a non-empty resolvedInvoiceGstNo in that
      // earlier log, the loss happened server-side, not in this form.
      try {
        logSequenceEvent(
          "gst-diag-create-customer-response",
          {
            customerId: result?.sCustomerId || customerId,
            finLinCustomerGst: (result?.finLinCustomer || []).map((f) => ({ gstNo: f?.gstNo, gstType: f?.gstType })),
            resultGstNo: result?.gstNo || null,
          },
          db
        );
      } catch (e) {
        // swallow — diagnostics must never affect customer creation
      }

      const groupedMetaData = isB2BEnabled ? extractGroupedMetaData(updatedMetaData) : undefined;

      // Extract customerProperties from response metaData (same pattern as updateCustomer).
      // data?.customerProperties may be undefined if the drawer footer didn't attach them,
      // so fall back to parsing the "Customer Properties" metaData entry from the server.
      let createdCustomerProperties = data?.customerProperties;
      if (Array.isArray(updatedMetaData)) {
        const cpEntry = updatedMetaData.find((m) => m.key === "Customer Properties");
        if (cpEntry?.value) {
          try {
            const parsed = typeof cpEntry.value === "string" ? JSON.parse(cpEntry.value) : cpEntry.value;
            if (parsed && typeof parsed === "object") {
              createdCustomerProperties = { ...(createdCustomerProperties || {}), ...parsed };
            }
          } catch (e) {
            /* keep existing */
          }
        }
      }
      const baseCart = isExchange ? useCartStore.getState().exchangeCart || cart : cart;
      // billingAddressId/shippingAddressId (computed above via
      // buildCustomerListPayload) are already the exact ids just sent to the
      // backend for this customer, in scope right here -- attach them
      // directly instead of round-tripping through localStorage (previously
      // gated to the /estimate route only, so this never fired on the normal
      // POS flow at all). This lets a later edit of this SAME customer in
      // this SAME cart reuse them via buildCustomerListPayload's
      // existingCustomerAddressIds fallback instead of minting a fresh uuid
      // every time -- the live-confirmed KWC/GPR address-fabrication bug.
      const customerAddressIds =
        isB2BEnabled && (billingAddressId || shippingAddressId)
          ? { billingId: billingAddressId, shippingId: shippingAddressId, invoiceSnapshot: invoiceContentSnapshot, deliverySnapshot: deliveryContentSnapshot }
          : null;
      const updatedCart = {
        ...baseCart,
        customer: {
          ...result,
          customerProperties: createdCustomerProperties,
          metaData: updatedMetaData,
          ...(groupedMetaData && { groupedMetaData }),
          sPriceListId: selectedPriceListId,
          newB2bCustomerId: customerId,
          nriBilling: isNriBilling,
          // BUG-2370: sent once more on this bill's posOrderProcessor1 metaData.
          customerChangeLog: customerChangeLog || null,
          ...(window.location.pathname.includes("/estimate") && localStorage.getItem("isB2bEnabled") === "true" ? { isB2bEnabled: "Y" } : {}),
          ...(customerAddressIds ? { customerAddressIds } : {}),
        },
      };
      if (updatedCart.items?.length > 0) {
        const precision = useAuthStore.getState().tillData?.tillAccess?.csBunit?.currencies?.[0]?.prcPrecision ?? 2;
        const isInterState = getIsInterState(updatedCart);
        updatedCart.groupedItemsByTaxRate = groupItemsByTaxRate(updatedCart.items, precision, { isInterState });
      }
      if (isExchange) {
        useCartStore.getState().setExchangeCart(updatedCart);
      } else {
        setCart(updatedCart);
      }

      // Show success message BEFORE closing drawer so user sees feedback
      message.success(result.message || "Customer created successfully");
      checkB2BSaveOnServer({
        flow: "create",
        isB2BEnabled,
        gstin: customerPayload?.[0]?.finLinCustomer?.[0]?.gstNo || customerPayload?.[0]?.gstNo,
        sCustomerId: customerId,
        billingId: billingAddressId,
        shippingId: shippingAddressId,
      });
      closeCustomerSearch();
      setIsNriBilling(false);
      if (!isCreditSale) {
        setIsCreditSale(false);
        localStorage.removeItem("isCreditSale");
        useTransactionStore.getState().setIsCreditSale(null);
      }
      if (isCallCenterFlow && tillLayout === 2) {
        history.push("/call-center");
        setCallCenterVisible(true);
      }
      const logConfig = await db.logConfiguration.toArray();
      const logCustomerAdd = logConfig.some((config) => config.addCustomer.some((item) => item.log === "Y"));

      if (logCustomerAdd) {
        posLogActivity(result.name, "ACT");
      }
      setCustomerLoading(false);
    } else {
      // Prefer the server's own explanation (mutation-level message, or a top-level
      // GraphQL error) over the generic fallback so the user sees why creation actually failed.
      const serverError = result?.message || response?.data?.errors?.[0]?.message;
      if (isDuplicateCustomerError(serverError)) {
        message.error(t("customer_already_exists_error"));
      } else {
        message.error(serverError || t("customer_edit_error"));
      }
      setCustomerLoading(false);
      setLastSearchedValue("");
      if (typeof setGstInfo === "function") setGstInfo(null);
      if (setSelectedInvoiceAddress) setSelectedInvoiceAddress(null);
      if (setSelectedDeliveryAddress) setSelectedDeliveryAddress(null);
      setPanCustomerInfo(null);
    }
  } catch (error) {
    Sentry.captureException(new Error("Create customer failed"), {
      extra: newRequest,
    });
    // Same network-failure classification already used by Sync/index.js:60 — a real
    // transport/connectivity failure gets the same "Sync Failed" popup used everywhere
    // else in the app (Full Sync, cart buttons, etc.), instead of a raw axios message
    // like "Network Error" in a plain toast. Any other failure (validation, server
    // rejection, timeout with a real response) shows the actual error text.
    const isNetworkError = !navigator.onLine || error?.message?.includes("Network Error") || error?.code === "ERR_NETWORK";
    if (isNetworkError) {
      Modal.error({ title: "Sync Failed", content: "Please check your network connection and try again" });
    } else {
      const actualError = error?.response?.data?.errors?.[0]?.message || error?.response?.data?.message || error?.message;
      if (isDuplicateCustomerError(actualError)) {
        message.error(t("customer_already_exists_error"));
      } else {
        message.error(actualError || t("customer_edit_error"));
      }
    }
    setCustomerLoading(false);
    setLastSearchedValue("");
    if (typeof setGstInfo === "function") setGstInfo(null);
    if (setSelectedInvoiceAddress) setSelectedInvoiceAddress(null);
    if (setSelectedDeliveryAddress) setSelectedDeliveryAddress(null);
    setPanCustomerInfo(null);
  }
};

export const updateCustomer = async (
  data,
  cart,
  setCart,
  form,
  closeCustomerSearch,
  selectedEditOldCustomer,
  setSelectedEditOldCustomer,
  setDisplayEditOldCustomer,
  tillData,
  customer,
  UAECustomerForm,
  setDisplayAddNewCustomer,
  setFormDataCache,
  setIsSubmittingCustomer,
  setLastSearchedValue,
  setSelectedB2BCustomer,
  selectedB2BCustomer,
  isCallCenterFlow,
  setCallCenterVisible,
  setIsCallCenterFlow,
  history,
  tillLayout,
  isPanCustomer,
  setIsPanCustomer,
  isB2BEnabled,
  setPanCustomerInfo,
  panCustomerInfo,
  pendingRefundCustomer,
  setPendingRefundCustomer,
  isRefundCustomerEdit,
  setIsRefundCustomerEdit,
  isRefundWalletOpen,
  setIsRefundWalletOpen,
  isNriBilling,
  setIsNriBilling,
  isCreditSale,
  setIsCreditSale,
  panDrawerMode,
  documents,
  setGstInfo,
  gstInfo,
  setExchangeCartTcs,
  selectedInvoiceAddress,
  selectedDeliveryAddress,
  setSelectedInvoiceAddress,
  setSelectedDeliveryAddress,
  switchToggle,
  setSwitchToggle
) => {
  const isExchange = useTransactionStore.getState().selectedReturnType === "EXCHANGE";
  const activeCustomer = isExchange ? useCartStore.getState().exchangeCart?.customer || selectedEditOldCustomer || cart?.customer : cart?.customer;
  const { retailCustomerGroups, retailCustomerFields, retailCustomerTabs, businessTabIds, businessGroupIds } = getFreshConfig();
  const creditLimitValue = resolveCreditLimitValue(data, retailCustomerFields);
  const t = i18next.t;
  let setAuthTokens;
  const authHeaders = await getOAuthHeaders();
  if (data && typeof data === "object") {
    if (data["Tax Id"] && !data.taxId) data.taxId = data["Tax Id"];
    if (data.taxid && !data.taxId) data.taxId = data.taxid;
    if (data.taxID && !data.taxId) data.taxId = data.taxID;
    // create consistent uppercase variant if server sometimes expects that
    if (data.taxId && !data.taxID) data.taxID = data.taxId;
  }

  if (authHeaders && authHeaders.access_token) {
    setAuthTokens = authHeaders.access_token;
  }

  function toNormalCase(key) {
    return key
      .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
      .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
      .replace(/^./, (str) => str.toUpperCase());
  }

  const ungroupedBusinessFields = retailCustomerFields.filter((field) => businessTabIds.includes(field.tabId) && (!field.groupId || !businessGroupIds.includes(field.groupId)));
  const EXCLUDED_META_KEYS = new Set([
    "customerdocumenturls",
    "pancardurl",
    "customer_document_urls",
    "pan_card_url",
    "passporturl",
    "passport_url",
    "sCustomer",
    "cwrcustomerid",
    "spricelistid",
    "csbunit",
    "customeraddress",
    "customeraddressids",
    "cwrwallet",
    "scustomer",
    "b2ccustomersegment",
    "creditapproachpercentage",
    "creditbalance",
    "creditlimit",
    "iscredit",
    "retlloyaltybalance",
    "b2cregisteredstoreid",
    "code",
    "lastbillingamount",
    "noofvisits",
    "averagebasketvalue",
    "pricelistname",
    "metadata",
    "nriBilling",
    "customerchangelog",
  ]);
  const usedKeys = getUsedKeys(retailCustomerGroups, retailCustomerTabs);
  const ungroupedBusinessKeys = ungroupedBusinessFields.map((field) => toCamelCase(field.name));
  const businessFieldKeysSet = new Set(ungroupedBusinessKeys.map((k) => k.toLowerCase()));
  const metaData = Object.entries(data)
    .filter(([key, value]) => {
      const normalizedKey = key.trim().toLowerCase().replace(/[_\s]/g, "");
      const lowerKey = key.toLowerCase();
      // grouped business fields
      const isGroupedBusinessField = businessGroupIds.includes(String(key));
      // ungrouped business fields
      const isUngroupedBusinessField = businessFieldKeysSet.has(lowerKey);
      const isBusinessField = isGroupedBusinessField || isUngroupedBusinessField;
      // MAIN CONDITION
      // if (isBusinessField && !(isPanCustomer && isB2BEnabled)) {
      //   return false;
      // }
      // if (isUngroupedBusinessField && !(isPanCustomer && isB2BEnabled)) {
      //   return false;
      // }
      return !usedKeys.has(key.trim().toLowerCase()) && !EXCLUDED_META_KEYS.has(normalizedKey) && value != null && value !== "";
    })
    .map(([key, value]) => {
      let normalizedKey = key.trim();
      if (/^group_\d+$/i.test(normalizedKey)) {
        normalizedKey = normalizedKey.toLowerCase();
      } else {
        normalizedKey = toNormalCase(key);
      }
      return {
        key: normalizedKey,
        value: typeof value === "object" ? JSON.stringify(value) : typeof value === "string" ? value.trim() : String(value),
      };
    });
  // Merge existing + newly uploaded documents
  // BUILD CUSTOMER DOCUMENT URLS (merge existing + new)
  const existingDocUrls = (() => {
    const meta = activeCustomer?.metaData || selectedEditOldCustomer?.metaData || [];
    const docMeta = meta.find((m) => m.key === "CUSTOMER_DOCUMENT_URLS");
    if (!docMeta?.value) return [];
    try {
      return JSON.parse(docMeta.value);
    } catch {
      return [];
    }
  })();

  const newDocUrls = documents.filter((doc) => doc?.docUrl && doc.docName === "Customer Document").map((doc) => doc.docUrl);

  // Merge: existing first, then new ones (deduplicated)
  const allDocUrls = [...new Set([...existingDocUrls, ...newDocUrls])];

  if (allDocUrls.length > 0) {
    metaData.push({
      key: "CUSTOMER_DOCUMENT_URLS",
      value: JSON.stringify(allDocUrls),
    });
  }
  // PAN CARD URL (preserve existing if no new upload)
  const newPanDoc = documents.find((doc) => doc.docName === "PAN Card");

  if (newPanDoc?.docUrl) {
    // User uploaded a new PAN card
    metaData.push({
      key: "PAN_CARD_URL",
      value: newPanDoc.docUrl,
    });
  } else {
    // Preserve existing PAN card URL from cart/customer
    const meta = activeCustomer?.metaData || selectedEditOldCustomer?.metaData || [];
    const existingPan = meta.find((m) => m.key === "PAN_CARD_URL");
    if (existingPan?.value) {
      metaData.push({
        key: "PAN_CARD_URL",
        value: existingPan.value,
      });
    }
  }

  const newPassportDoc = documents.find((doc) => doc.docName === "Passport");

  if (newPassportDoc?.docUrl) {
    metaData.push({
      key: "PASSPORT_URL",
      value: newPassportDoc.docUrl,
    });
  } else {
    // Preserve existing passport URL
    const meta = activeCustomer?.metaData || selectedEditOldCustomer?.metaData || [];
    const existingPassport = meta.find((m) => m.key === "PASSPORT_URL");
    if (existingPassport?.value) {
      metaData.push({
        key: "PASSPORT_URL",
        value: existingPassport.value,
      });
    }
  }

  const ungroupedFieldsObj = ungroupedBusinessFields.reduce((acc, field) => {
    const camelCaseKey = toCamelCase(field.name);
    const value = data[camelCaseKey];
    if (value !== "" && value !== null && value !== undefined) {
      acc[field.name?.trim() || field.id] = String(value);
    }
    return acc;
  }, {});

  const groupedCustomerListObj = Object.entries(data)
    .filter(([key]) => businessGroupIds.includes(String(key)))
    .reduce((acc, [groupId, groupData]) => {
      const group = retailCustomerGroups.find((g) => g.id === groupId);
      const groupName = group?.name?.trim() || "";

      if (typeof groupData === "object" && groupData !== null) {
        const cleanedFields = Object.entries(groupData).reduce((fieldAcc, [fieldKey, fieldValue]) => {
          if (fieldValue !== "" && fieldValue !== null && fieldValue !== undefined) {
            fieldAcc[fieldKey.trim()] = String(fieldValue);
          }
          return fieldAcc;
        }, {});

        if (Object.keys(cleanedFields).length > 0) {
          acc[groupName] = cleanedFields;
        }
      }
      return acc;
    }, {});

  let customerListObj = {};

  if (isPanCustomer) {
    customerListObj = Object.entries(data).reduce((acc, [key, value]) => {
      if (value !== "" && value !== null && value !== undefined) {
        acc[key] = String(value);
      }
      return acc;
    }, {});
  } else {
    customerListObj = {
      ...ungroupedFieldsObj,
      ...groupedCustomerListObj,
    };
  }

  // const stringifiedData = JSON.stringify(customerListObj);
  // const newStringifiedFields = stringifiedData.replace(/\\"/g, '\\"');
  // const newStringRequest = JSON.stringify(newStringifiedFields);

  let customerListPayload = null;
  let invoiceAddressId = null;
  let deliveryAddressId = null;
  let newB2bCustomerId = null;
  let invoiceContentSnapshot = null;
  let deliveryContentSnapshot = null;
  if (isB2BEnabled) {
    const activeCartForCustomerAnchor = isExchange ? useCartStore.getState().exchangeCart || cart : cart;
    const payloadData = buildCustomerListPayload(
      data,
      selectedB2BCustomer,
      panCustomerInfo,
      tillData,
      gstInfo,
      isPanCustomer,
      isB2BEnabled,
      selectedInvoiceAddress,
      selectedDeliveryAddress,
      switchToggle,
      activeCartForCustomerAnchor?.sOrderID,
      // Same fallback as createCustomer -- reuse this cart's already-known
      // address ids (attached below after this update succeeds) instead of
      // minting a fresh one on every edit.
      activeCartForCustomerAnchor?.customer?.customerAddressIds
    );
    customerListPayload = payloadData.customerPayload;
    invoiceAddressId = payloadData.invoiceAddressId;
    deliveryAddressId = payloadData.deliveryAddressId;
    newB2bCustomerId = payloadData.customerId;
    invoiceContentSnapshot = payloadData.invoiceContentSnapshot;
    deliveryContentSnapshot = payloadData.deliveryContentSnapshot;
  }
  const newStringRequest = buildGraphQLJSONString(customerListPayload);

  // BUG-2370: show the cashier Before / After before the B2B flag is written.
  // "Go back and edit" leaves the drawer and form as they are.
  const reviewOldCustomer = selectedEditOldCustomer || activeCustomer || null;
  const { confirmed: reviewConfirmed, changeLog: customerChangeLog } = await reviewCustomerSave({
    mode: "update",
    data,
    payloadEntry: customerListPayload?.[0],
    isB2BEnabled,
    switchToggle,
    tillData,
    oldCustomer: reviewOldCustomer,
    wasB2B: useTransactionStore.getState().isB2bEnabled === "true" || !!activeCustomer?.customerAddressIds,
    selectedB2BCustomer,
    selectedInvoiceAddress,
    selectedDeliveryAddress,
    fields: retailCustomerFields,
    isPanCustomer,
    isNriBilling,
    isCreditSale,
  });
  if (!reviewConfirmed) return false;
  // BUG-2370: from / to of what the cashier confirmed in the review popup.
  if (customerChangeLog) metaData.push({ key: CHANGE_LOG_META_KEY, value: customerChangeLog });

  // ADDR-DIAG: exact customerAddress array as it's about to be serialized
  // into the `customerList` string sent to upsertB2CCustomer (update path).
  try {
    const wirePayload = { flow: "update", customerAddress: customerListPayload?.[0]?.customerAddress || null };
    logSequenceEvent("addr-diag-wire-payload", wirePayload, db);
    trace("AddrDiag", "wire-payload", wirePayload);
  } catch (e) {
    // swallow — diagnostics must never affect customer save
  }

  if (isB2BEnabled) {
    localStorage.setItem("isB2bEnabled", "true");
    useTransactionStore.getState().setIsB2bEnabled(String(isB2BEnabled));
  } else {
    localStorage.removeItem("isB2bEnabled");
    useTransactionStore.getState().setIsB2bEnabled(null);
  }

  if (isCreditSale) {
    useTransactionStore.getState().setIsCreditSale(String(isCreditSale));
  } else {
    localStorage.removeItem("isCreditSale");
    useTransactionStore.getState().setIsCreditSale(null);
  }

  if (isPanCustomer) {
    localStorage.setItem("isPanCustomer", String(isPanCustomer));
  }

  if (isNriBilling) {
    localStorage.setItem("isNriBilling", String(isNriBilling));
  }

  const posConfig = usePOSConfigStore.getState().posConfig;
  const serverUrl = process.env.REACT_APP_serverUrl;
  let customerId = selectedEditOldCustomer?.cwrCustomerId || (activeCustomer?.b2cRegisteredstoreId !== null ? activeCustomer?.cwrCustomerId : null);
  let resolvedSCustomerId = null;
  if (isB2BEnabled) {
    const newsCustomerId = selectedB2BCustomer ? selectedB2BCustomer?.customerId : activeCustomer?.sCustomer?.sCustomerID;
    resolvedSCustomerId = newsCustomerId || null;
  }
  // const loyaltyLevel =
  // retailCustomerFields && retailCustomerFields.length > 0 ? data.loyaltyLevel : data.program && data.program !== "" && data.loyality === true ? data.program : null;
  const loyaltyLevel =
    retailCustomerFields && retailCustomerFields.length > 0
      ? data.loyaltyLevel?.cwrLoyaltyLevelId
      : data.program && data.program !== "" && data.loyality === true
        ? data.program
        : null;

  const oldMobile = selectedEditOldCustomer?.mobileNo?.trim();
  const newMobile = (data.mobileno || data.editMobile || "").trim();

  const isMobileChanged = newMobile && newMobile !== oldMobile;

  if (isMobileChanged) {
    const paramsInput = {
      query: `query{
      customerSearch(
        searchKey:"${newMobile}",
        bunitId:"${tillData?.tillAccess?.csBunit?.csBunitId}"
      ){
        cwrCustomerId
        name
        mobileNo
      }
    }`,
    };

    try {
      const response = await getCustomerService(paramsInput);
      const searchResults = response?.data?.data?.customerSearch || [];

      // ✅ If found AND not same customer
      const duplicateCustomer = searchResults.find((cust) => cust.cwrCustomerId !== selectedEditOldCustomer?.cwrCustomerId);

      if (duplicateCustomer) {
        message.warning(`Mobile number already registered with customer "${duplicateCustomer.name}"`);
        return; // ⛔ STOP update
      }
    } catch (error) {
      Sentry.captureException(error);
      message.error("Unable to validate mobile number.");
      return; // ⛔ Stop update
    }
  }
  const contactAddress = getAddressFromContactGroup(data, retailCustomerGroups, retailCustomerTabs);

  // Resolve a raw state value (RecordID or name text) to a valid RecordID from statesData.
  // Returns the RecordID string, or empty string if not found.
  const resolveStateId = (stateVal, statesData) => {
    if (!stateVal) return "";
    if (statesData.some((s) => s.RecordID === stateVal)) return stateVal;
    const byName = statesData.find((s) => s.Name?.toLowerCase() === stateVal.toLowerCase());
    return byName?.RecordID || "";
  };

  // Extract state RecordID from group_NN form fields. Accepts any valid RecordID
  // or state name — resolves via statesData so format/length restrictions do not block.
  const getStateIdFromGroups = (data) => {
    const statesData = useReferenceStore.getState().statesData || [];
    for (const [key, value] of Object.entries(data)) {
      if (/^group_\d+$/.test(key)) {
        try {
          const parsed = typeof value === "string" ? JSON.parse(value) : value;
          const resolved = resolveStateId(parsed?.state, statesData);
          if (resolved) return resolved;
        } catch (err) {
          console.error("[Customer] Failed to parse state metadata:", err);
        }
      }
    }
    return null;
  };

  // Fallback: look for a flat "state"/"csRegionId" key in the existing customer's
  // metaData, then customerAddress.csRegionId. Uses statesData validation so any
  // valid ID format or name is accepted.
  const getRegionFromMetadata = () => {
    const statesData = useReferenceStore.getState().statesData || [];
    const meta = selectedEditOldCustomer?.metaData || activeCustomer?.metaData || [];
    const stateMeta = meta.find((m) => m.key === "state" || m.key === "csRegionId");
    if (stateMeta?.value) {
      const resolved = resolveStateId(stateMeta.value, statesData);
      if (resolved) return resolved;
    }
    return selectedEditOldCustomer?.customerAddress?.csRegionId || activeCustomer?.customerAddress?.csRegionId || null;
  };

  const selectedStateId =
    getStateIdFromGroups(data) || data?.customerAddress?.csRegionId || getRegionFromMetadata() || tillData?.tillAccess?.csBunit?.customerAddress?.csRegion?.csRegionID || null;

  // Trim leading/trailing whitespace before it reaches the mutation payload (BUG-1884).
  const trimIfString = (value) => (typeof value === "string" ? value.trim() : value);
  const trimmedLastName = trimIfString(data.lastName);

  const isExistingCustomer = !!customerId;
  const newRequest = {
    query: `mutation{
              upsertB2CCustomer(id: "${customerId || ""}",
                type:"${isExistingCustomer ? "Old" : "New"}",
                customer: {
                  csBunitId: "${tillData.tillAccess.csBunit.csBunitId}",
                  createdby: "${tillData.tillAccess.csUserId}",
                  csClientId: "${tillData.tillAccess.csClientId}",
                  updatedby: "${tillData.tillAccess.csUserId}",
                  code: null
                 ${(() => {
                   const rawName = trimIfString(data?.name || data?.customerName || data?.editName || selectedEditOldCustomer?.name || customer?.name || customer?.firstName || "");
                   const resolvedFirstName = trimIfString(data?.firstName || data?.editName || selectedEditOldCustomer?.firstName || customer?.firstName || rawName || "");
                   // Rebuild from the trimmed parts (BUG-1884) instead of trimming rawName as a
                   // whole, so interior whitespace from an untrimmed First/Last Name can't survive.
                   const resolvedName = [resolvedFirstName, trimmedLastName].filter(Boolean).join(" ") || rawName;
                   return `name: ${resolvedName ? `"${resolvedName}"` : null},
                            firstName: ${resolvedFirstName ? `"${resolvedFirstName}"` : null},`;
                 })()}
                  mobileNo: ${data.mobileno !== undefined || data.editMobile !== undefined ? `"${data.mobileno || data.editMobile || ""}"` : null},
                  email: ${data.email || data.editEmail ? `"${data.email || data.editEmail}"` : null},
                  pincode: ${data.pincode || data.editPincode ? data.pincode || data.editPincode : null},
                  validity: ${data.validity ? `"${dayjs(data.validity).format("YYYY-MM-DD")}"` : null},
                  regType: ${data.type ? `"${data.type}"` : null},
                  cardNumber: ${data.cardNumber ? `"${data.cardNumber}"` : null},
                  sCustomerId: ${resolvedSCustomerId ? `"${resolvedSCustomerId}"` : null},
                  finPaymentmethodId: null,
                  retlLoyaltyBalance: null,
                  iscredit: ${isCreditSale ? "true" : "false"},
                  creditLimit: ${isCreditSale && creditLimitValue !== null ? creditLimitValue : null},
                  b2cRegisteredstoreId: "${tillData.tillAccess.csBunit.csBunitId}",
                  lastName: ${trimmedLastName ? `"${trimmedLastName}"` : null},
                  gender: ${data.gender && data.gender !== "null" ? `"${data.gender}"` : null},
                  taxId: ${
                    data.taxId || data.taxID || selectedB2BCustomer?.taxId || selectedEditOldCustomer?.taxId
                      ? `"${(data.taxId || data.taxID || selectedB2BCustomer?.taxId || selectedEditOldCustomer?.taxId).replace(/"/g, '\\"')}"`
                      : null
                  },
                  anniversaryDate: ${
                    data.anniversaryDate !== undefined && data.anniversaryDate !== "" && data.anniversaryDate !== null
                      ? `"${dayjs(data.anniversaryDate).format("YYYY-MM-DD")}"`
                      : null
                  },
                  birthday: ${data.BirthdayDate || data.birthday ? `"${dayjs(data.BirthdayDate || data.birthday).format("YYYY-MM-DD")}"` : null},
                  lastVisitDate: ${data.lastVisitDate !== undefined && data.lastVisitDate !== null ? `"${dayjs(data.lastVisitDate).format("YYYY-MM-DD")}"` : null},
                  noOfVisits: 0,
                  lastBillingAmount: 0,
                  averageBasketValue: 0,
                  loyaltyPointToExpire: null,
                  metaData: ${JSON.stringify(metaData)
                    .replace(/"key":/g, "key:")
                    .replace(/"value":/g, "value:")
                    .replace(/"/g, '"')},
                  loyaltyLevel: { cwrLoyaltyLevelId:  ${loyaltyLevel && loyaltyLevel !== null && loyaltyLevel !== "undefined" ? `"${loyaltyLevel}"` : null} }
                  customerAddress: {
                        line1: ${retailCustomerFields && retailCustomerFields.length > 0 ? (contactAddress?.line1 ? `"${contactAddress.line1}"` : data.street ? `"${data.street}"` : null) : data.street ? `"${data.street}"` : null},
                        line2: ${retailCustomerFields && retailCustomerFields.length > 0 ? (contactAddress?.line2 ? `"${contactAddress.line2}"` : null) : data.city ? `"${data.city}"` : null},
                        city: ${retailCustomerFields && retailCustomerFields.length > 0 ? (contactAddress?.city ? `"${contactAddress.city}"` : data.city ? `"${data.city}"` : null) : data.city ? `"${data.city}"` : null},
                        csRegionId: ${selectedStateId ? `"${selectedStateId}"` : null},
                        csCountryId: ${tillData.tillAccess.csBunit.customerAddress?.csCountry?.csCountryID ? `"${tillData.tillAccess.csBunit.customerAddress.csCountry.csCountryID}"` : null},
                  }
                  b2bCustomer: "${isB2BEnabled ? "Y" : "N"}",
                  customerList: "${newStringRequest}"            
                }){
                cwrCustomerId
                sPriceListId
                code
                name
                email
                mobileNo
                pincode
                retlLoyaltyBalance
                b2cRegisteredstoreId
                iscredit
                creditLimit
                creditBalance
                lastVisitDate
                noOfVisits
                lastBillingAmount
                averageBasketValue
                loyaltyPointToExpire
                firstName
                lastName
                gender
                taxId
                cardNumber
                regType
                validity
                anniversaryDate
                birthday
                sCustomer {
                  sCustomerID
                  customerCategory {
                    sCustomerCateforyId
                    value
                    name
                    description
                  }
                }
                csBunit {
                  csBunitId
                  name
                }
                customerAddress {
                  line1
                  line2
                  city
                  region
                  country
                  csCountryId
                  csRegionId
                }
                loyaltyLevel { 
                  cwrLoyaltyLevelId}
                status
                message
                metaData { 
                  key
                  value
                }
              }
            }`,
  };

  try {
    const response = await updateCustomerService(newRequest);

    const result = response?.data?.data?.upsertB2CCustomer;
    // BUG-1511: upsertB2CCustomer does not echo back B2B group metaData in its response
    // (B2B address/invoice groups are stored in a separate table). Use the locally-computed
    // metaData (what we just submitted) when the server returns empty and B2B is enabled,
    // so getIsInterState can resolve the customer state immediately without a re-fetch.
    const updatedMetaData = result?.metaData?.length > 0 ? result.metaData : isB2BEnabled ? metaData : [];
    const groupedMetaData = isB2BEnabled ? extractGroupedMetaData(updatedMetaData) : null;
    if (result?.status === "200") {
      form.resetFields();
      UAECustomerForm.resetFields();
      setSelectedEditOldCustomer();
      setDisplayEditOldCustomer(false);
      setDisplayAddNewCustomer(false);
      setFormDataCache({});
      if (panDrawerMode !== "VALIDATION") {
        setPanCustomerInfo(null);
        setIsPanCustomer(false);
      }
      if (isB2BEnabled) {
        // BUG-2038: always overwrite (not just when unset) — see matching
        // comment at the createCustomer write site above. sOrderID stamped
        // for the same cross-cart-reuse-scoping reason (buildCustomerListPayload).
        const activeCartForCustomerAnchor = isExchange ? useCartStore.getState().exchangeCart || cart : cart;
        localStorage.setItem(
          "customerAddressIds",
          JSON.stringify({
            customerId: newB2bCustomerId,
            billingId: invoiceAddressId,
            shippingId: deliveryAddressId,
            sOrderID: activeCartForCustomerAnchor?.sOrderID,
            invoiceSnapshot: invoiceContentSnapshot,
            deliverySnapshot: deliveryContentSnapshot,
          })
        );
      } else {
        localStorage.removeItem("customerAddressIds");
      }
      setIsSubmittingCustomer(false);
      setLastSearchedValue("");
      if (typeof setGstInfo === "function") setGstInfo(null);
      if (setSelectedInvoiceAddress) setSelectedInvoiceAddress(null);
      if (setSelectedDeliveryAddress) setSelectedDeliveryAddress(null);
      const saleTypeData = usePOSConfigStore.getState().saleTypeData || {};
      const selectedPriceListId = result?.sPriceListId ?? saleTypeData?.customer?.sPriceListId;
      // Patch invoiceAddresses / deliveryAddresses / localisation with the
      // updated customerAddress fields. The upsertB2CCustomer response only
      // returns the single customerAddress, but the cart customer holds the
      // full invoiceAddresses[]/deliveryAddresses[]/localisation[] arrays.
      // Without this patch, view/edit reads stale region/city/etc from those
      // arrays after an update. (Issue #15)
      const patchAddressArray = (arr) => {
        if (!Array.isArray(arr) || arr.length === 0 || !result?.customerAddress) return arr;
        const updatedFirst = {
          ...arr[0],
          line1: result.customerAddress.line1 ?? arr[0].line1,
          line2: result.customerAddress.line2 ?? arr[0].line2,
          city: result.customerAddress.city ?? arr[0].city,
          regionName: result.customerAddress.region ?? arr[0].regionName,
          countryName: result.customerAddress.country ?? arr[0].countryName,
          pincode: result.pincode ?? arr[0].pincode,
        };
        return [updatedFirst, ...arr.slice(1)];
      };
      const patchedInvoiceAddresses = patchAddressArray(customer?.invoiceAddresses);
      const patchedDeliveryAddresses = patchAddressArray(customer?.deliveryAddresses);

      // Extract customerProperties from response metaData if present.
      // The upsertB2C response encodes PAN validation results inside
      // metaData[{key:"Customer Properties", value:"...JSON..."}].
      // Without extracting this, cart.customer.customerProperties stays
      // stale and TCS / PAN status checks fail. (Issues #3, #9, #11)
      let updatedCustomerProperties = customer?.customerProperties || data?.customerProperties;
      if (Array.isArray(updatedMetaData)) {
        const cpEntry = updatedMetaData.find((m) => m.key === "Customer Properties");
        if (cpEntry?.value) {
          try {
            const parsed = typeof cpEntry.value === "string" ? JSON.parse(cpEntry.value) : cpEntry.value;
            if (parsed && typeof parsed === "object") {
              updatedCustomerProperties = { ...(updatedCustomerProperties || {}), ...parsed };
            }
          } catch (e) {
            /* keep existing */
          }
        }
      }
      // invoiceAddressId/deliveryAddressId (computed above via
      // buildCustomerListPayload) are already the exact ids just sent to the
      // backend for this update, in scope right here -- attach them directly
      // instead of round-tripping through localStorage (previously gated to
      // the /estimate route only). Same fix as the createCustomer site above.
      const customerAddressIds =
        isB2BEnabled && (invoiceAddressId || deliveryAddressId)
          ? { billingId: invoiceAddressId, shippingId: deliveryAddressId, invoiceSnapshot: invoiceContentSnapshot, deliverySnapshot: deliveryContentSnapshot }
          : null;
      // GST-DIAG (BUG-1682/GPR-IRN): "edit/change details" touchpoint — what the
      // backend saved/echoed back on update, same check as createCustomer's
      // gst-diag-create-customer-response, closing the loop for the edit path too.
      try {
        logSequenceEvent(
          "gst-diag-update-customer-response",
          {
            customerId: result?.sCustomerId || customer?.sCustomerId,
            finLinCustomerGst: (result?.finLinCustomer || []).map((f) => ({ gstNo: f?.gstNo, gstType: f?.gstType })),
            resultGstNo: result?.gstNo || null,
            priorCustomerGstNo: customer?.gstNo || null,
          },
          db
        );
      } catch (e) {
        // swallow — diagnostics must never affect customer update
      }
      const updatedCustomer = {
        ...result,
        // Fall back to the cart's existing customer ID if the upsert response
        // omits cwrCustomerId (seen on PAN-flagged updates, which send
        // b2bCustomer:"Y"). Without this, SMCustomerBar's isDefaultCustomer
        // check sees no cwrCustomerId and reverts the bar to the default
        // "Enter customer number/name" search input even though the update
        // succeeded.
        cwrCustomerId: result?.cwrCustomerId || customer?.cwrCustomerId,
        newB2bCustomerId: newB2bCustomerId,
        sCustomer: {
          ...result?.sCustomer,
          sCustomerID: data?.b2bCustomerId || result?.sCustomer?.sCustomerID || null,
        },
        // Use response values with fallback to old cart values. Previously
        // these were unconditionally set to old values, so response updates
        // for loyalty, credit, wallet, etc. were silently discarded.
        // (Issues #2, #3, #5, #9, #13)
        customerProperties: updatedCustomerProperties,
        loyaltyLevel: result?.loyaltyLevel ?? customer?.loyaltyLevel,
        b2cCustomerSegment: result?.b2cCustomerSegment ?? customer?.b2cCustomerSegment,
        creditApproachPercentage: result?.creditApproachPercentage ?? customer?.creditApproachPercentage,
        creditBalance: result?.creditBalance ?? customer?.creditBalance,
        creditLimit: result?.creditLimit ?? customer?.creditLimit,
        cwrWallet: customer?.cwrWallet,
        iscredit: result?.iscredit ?? customer?.iscredit,
        priceListName: result?.priceListName ?? customer?.priceListName,
        lastBillingAmount: result?.lastBillingAmount ?? customer?.lastBillingAmount,
        lastVisitDate: result?.lastVisitDate ?? customer?.lastVisitDate,
        noOfVisits: result?.noOfVisits ?? customer?.noOfVisits,
        retlLoyaltyBalance: result?.retlLoyaltyBalance ?? customer?.retlLoyaltyBalance,
        validity: result?.validity ?? customer?.validity,
        loyaltyPointToExpire: result?.loyaltyPointToExpire ?? customer?.loyaltyPointToExpire,
        // Prefer the user-typed values over the server response — fixes issue
        // #4 where the server's response sometimes had a stale `name` (e.g.
        // because firstName/lastName were re-joined server-side) leaving the
        // UI showing the old name even though the user just typed a new one.
        firstName: data?.firstName || result.firstName,
        lastName: data?.lastName || result.lastName,
        name: data?.name || data?.customerName || data?.editName || result.name || result.firstName || selectedEditOldCustomer?.name || customer?.name,
        email: data?.email || data?.editEmail || result.email,
        customerAddress: result.customerAddress,
        invoiceAddresses: patchedInvoiceAddresses ?? customer?.invoiceAddresses,
        deliveryAddresses: patchedDeliveryAddresses ?? customer?.deliveryAddresses,
        birthday: result.birthday,
        mobileNo: result.mobileNo,
        pincode: result.pincode,
        anniversaryDate: result.anniversaryDate,
        gender: result.gender,
        metaData: updatedMetaData,
        sPriceListId: selectedPriceListId,
        nriBilling: isNriBilling,
        // BUG-2370: sent once more on this bill's posOrderProcessor1 metaData.
        customerChangeLog: customerChangeLog || null,
        ...(window.location.pathname.includes("/estimate") && localStorage.getItem("isB2bEnabled") === "true" ? { isB2bEnabled: "Y" } : {}),
        ...(customerAddressIds ? { customerAddressIds } : {}),
      };
      if (isB2BEnabled) {
        updatedCustomer.groupedMetaData = groupedMetaData;
      } else {
        delete updatedCustomer.groupedMetaData;
      }
      if (useTransactionStore.getState().refundCustomerEdit === "Y" && cart?.isReturn === true) {
        useTransactionStore.getState().setRefundReturnCustomerId(result);

        useTransactionStore.getState().setRefundCustomerEdit(null);
        setIsRefundWalletOpen(true); // reopen wallet drawer
        closeCustomerSearch();
        return;
      }
      const isExchangeType = useTransactionStore.getState().selectedReturnType === "EXCHANGE";
      const baseCart = isExchangeType ? useCartStore.getState().exchangeCart || cart : cart;
      const updatedCart = {
        ...baseCart,
        customer: {
          ...updatedCustomer,
          newB2bCustomerId: updatedCustomer?.newB2bCustomerId || selectedB2BCustomer?.customerId || baseCart?.customer?.sCustomer?.sCustomerID || baseCart?.customer?.newB2bCustomerId,
        },
      };
      if (updatedCart.items?.length > 0) {
        const precision = useAuthStore.getState().tillData?.tillAccess?.csBunit?.currencies?.[0]?.prcPrecision ?? 2;
        const isInterState = getIsInterState(updatedCart);
        updatedCart.groupedItemsByTaxRate = groupItemsByTaxRate(updatedCart.items, precision, { isInterState });
      }
      if (isExchangeType) {
        if (typeof setExchangeCartTcs === "function") setExchangeCartTcs(true);
        useCartStore.getState().setExchangeCart(updatedCart);
      } else {
        setCart(updatedCart);
      }
      // Show success message BEFORE closing the drawer so user sees feedback
      message.success(result.message || "Customer updated successfully");
      checkB2BSaveOnServer({
        flow: "update",
        isB2BEnabled,
        gstin: customerListPayload?.[0]?.finLinCustomer?.[0]?.gstNo || customerListPayload?.[0]?.gstNo,
        sCustomerId: newB2bCustomerId,
        billingId: invoiceAddressId,
        shippingId: deliveryAddressId,
      });
      closeCustomerSearch();
      setIsNriBilling(false);
      if (!isCreditSale) {
        setIsCreditSale(false);
        localStorage.removeItem("isCreditSale");
        useTransactionStore.getState().setIsCreditSale(null);
      }
      upsertPOSLog(updatedCart, "CSU");
    } else {
      setLastSearchedValue("");
      if (typeof setGstInfo === "function") setGstInfo(null);
      if (setSelectedInvoiceAddress) setSelectedInvoiceAddress(null);
      if (setSelectedDeliveryAddress) setSelectedDeliveryAddress(null);
      setPanCustomerInfo(null);
      message.error(t("customer_edit_error"));
    }
  } catch (error) {
    Sentry.captureException(new Error("Update customer failed"), {
      extra: newRequest,
    });
    setLastSearchedValue("");
    if (typeof setGstInfo === "function") setGstInfo(null);
    if (setSelectedInvoiceAddress) setSelectedInvoiceAddress(null);
    if (setSelectedDeliveryAddress) setSelectedDeliveryAddress(null);
    setPanCustomerInfo(null);
    console.error("Updating customer failed: ", error);
    message.error(t("customer_edit_error"));
  }
};

export const getCustomer = async (
  form,
  tillLayout,
  customerSearchInput,
  setKioskUI,
  setCustomerSearchResults,
  kioskLogin,
  tillData,
  pendingRefundCustomer,
  setPendingRefundCustomer,
  isRefundCustomerEdit,
  setIsRefundCustomerEdit,
  options
) => {
  const posConfig = usePOSConfigStore.getState().posConfig;
  let loginType;
  if (tillLayout !== 3) {
    if (
      !/^[a-zA-Z0-9.!#$%&'+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)$/.test(customerSearchInput) &&
      !RegExp("^[6-9][0-9]{9}$")?.test(parseFloat(customerSearchInput)) &&
      !parseFloat(customerSearchInput)
    ) {
      loginType = "name";
    }
  }

  if (/^[a-zA-Z0-9.!#$%&'+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)$/.test(customerSearchInput)) {
    loginType = "email";
  }

  if (/^[1-9]\d*$/.test(customerSearchInput)) {
    loginType = "mobile";
  }

  const paramsInput = {
    query: `query($searchKey: String!, $bunitId: String){
          customerSearch(searchKey:$searchKey,bunitId:$bunitId){
            cwrCustomerId
            sCustomerAddress
            localisation
            sPriceListId 
            code   
            name
            email        
            mobileNo
            sCustomerAddress
            cardNumber
            regType
            validity
            name
            creditLimit
            creditBalance
            creditApproachPercentage
            pincode
            retlLoyaltyBalance
            b2cRegisteredstoreId
             iscredit
             lastVisitDate
             noOfVisits
             lastBillingAmount
             averageBasketValue
             loyaltyPointToExpire
             firstName
              lastName
               gender
                taxId
            anniversaryDate
             birthday
            loyaltyLevel{
                cwrLoyaltyLevelId
                name
                accumulationRate
                redemptionRate
            }
            cwrWallet {
                 wallet_Id
                balance_Amt
            }
            sCustomer{
              sCustomerID
              customerCategory{
                sCustomerCateforyId
                value
                name
                description
              }
            }
            customerAddress {
                line1 
                line2 
                city 
                region
                 country 
                 csCountryId
                  csRegionId
            }
            csBunit{
              csBunitId
              name
            }
            b2cCustomerSegment{
              name
              cwrB2CCustomerSegmentId
            }
            sPriceListId
            priceListName
            metaData {
              key
              value
            }
          }
        }`,
    variables: {
      searchKey: customerSearchInput,
      bunitId: tillData?.tillAccess?.csBunit?.csBunitId || "",
    },
  };
  try {
    const response = await getCustomerService(paramsInput, options);

    if (options?.signal?.aborted) return response;

    localStorage.setItem("loginType", loginType);
    const customerSearchResults = response?.data?.data?.customerSearch;

    if (customerSearchResults?.length > 0) {
      try {
        const { retailCustomerFields, retailCustomerGroups, retailCustomerTabs } = getFreshConfig();

        const businessTabIds = retailCustomerTabs.filter((tab) => tab.name?.toLowerCase().includes("business")).map((tab) => tab.id);

        const businessFieldNames = retailCustomerFields.filter((field) => businessTabIds.includes(field.tabId)).map((field) => field.name);

        const businessGroupIds = retailCustomerGroups.filter((group) => businessTabIds.includes(group.tabId)).map((group) => group.id);

        const normalizeKey = (key) => key?.toLowerCase().replace(/[^a-z0-9]/g, "");

        const removeKeysSet = new Set([...businessFieldNames, ...businessGroupIds].map(normalizeKey));

        // BUG-1969 (F2): taxUtils.js getIsInterState() needs the Delivery Address group's
        // state to classify B2B interstate tax, but that group is stripped from metaData
        // below (BUG-1202, intentional -- keeps stale Business Tab data from reappearing in
        // Edit Customer if B2B is re-enabled). Confirmed against the live customerSearch SQL
        // (CustomerDaoImpl.java getCustomerSearch) that the backend returns this group's raw
        // value unfiltered -- only this client-side strip removes it. Capture just the state
        // value into a separate, non-metaData field BEFORE stripping so BUG-1202's fix is
        // untouched and the value survives into cart.customer for tax purposes.
        const deliveryAddressGroupId = retailCustomerGroups.find((g) => g.name === "Delivery Address")?.id;
        const extractDeliveryState = (customer) => {
          if (!deliveryAddressGroupId) return null;
          const metaRow = (customer.metaData || []).find((item) => item.key === deliveryAddressGroupId);
          if (!metaRow?.value) return null;
          try {
            const parsed = typeof metaRow.value === "string" ? JSON.parse(metaRow.value) : metaRow.value;
            return parsed?.state || null;
          } catch (e) {
            return null;
          }
        };

        const cleanedResults = customerSearchResults.map((customer) => {
          const filteredMetaData = (customer.metaData || []).filter((item) => {
            const normalizedItemKey = normalizeKey(item.key);
            return !removeKeysSet.has(normalizedItemKey);
          });

          return {
            ...customer,
            metaData: filteredMetaData,
            b2bDeliveryStateId: extractDeliveryState(customer),
          };
        });

        if (options?.signal?.aborted) return response;

        setCustomerSearchResults(cleanedResults);

        if (tillLayout === 3) {
          kioskLogin.resetFields();
          setKioskUI(3);

          const customEvent = new CustomEvent("customStorageChange", {
            detail: { key: "kioskUI", newValue: 3 },
          });

          window.dispatchEvent(customEvent);
          localStorage.setItem("kioskUI", 3);
        }
      } catch (error) {
        console.error("Error filtering business metadata:", error);
        if (!options?.signal?.aborted) {
          setCustomerSearchResults(customerSearchResults);
        }
      }
    } else {
      if (!options?.signal?.aborted) {
        setCustomerSearchResults([]);
      }

      if (tillLayout === 3) {
        form.setFieldsValue({
          [loginType]: customerSearchInput,
        });
        setKioskUI(4);
        const customEvent = new CustomEvent("customStorageChange", {
          detail: { key: "kioskUI", newValue: 4 },
        });
        window.dispatchEvent(customEvent);
        localStorage.setItem("kioskUI", 4);
      }
    }
    return response;
  } catch (error) {
    if (error.name === "CanceledError" || error.name === "AbortError") throw error;
    Sentry.captureException(error);
    console.error("Customer search failed:", error);
  }
};
